import { useCallback, useEffect } from 'react'
import { toast } from 'sonner'
import { useLanguage } from '@/lib/i18n/language-provider'
import type { DataPage, PowerPage, WorkflowStep, WallTechnicalConfig } from '@/types/synoptique'
import { sanitizeDataPage, sanitizePowerPage } from '../utils/cabling-mutations'
import posthog from 'posthog-js'

interface UseSaveLoadParams {
  // State values
  currentProject: { id: string; name: string } | null
  projectName: string
  currentStep: WorkflowStep
  hasVisitedPowerStep: boolean
  importedLEDConfig: any
  dataPages: DataPage[]
  currentDataPageId: string
  powerPages: PowerPage[]
  currentPowerPageId: string
  exportViewMode: string
  showCartouche: boolean
  cartouche: any
  backupPorts: Record<string, Record<number, number>>
  backupProcessors: Record<string, string>
  portColorOverrides: Record<string, Record<number, string>>
  outletColorOverrides: Record<string, Record<number, string>>
  savedMires: any
  projectMires: any[]
  mireBackgroundImage: string | null
  mireBackgroundOpacity: number
  wallTechnicalConfig: {
    colorDepth: 8 | 10 | 12 | 16
    refreshRate: 50 | 60
    hdrEnabled: boolean
    panelResolution?: { width: number; height: number }
    panelWeight?: number
    totalPanels?: number
    totalPixels?: number
    calculatedPortCapacity?: number
  }
  localFilePath: string | null
  saveAsFileName: string
  isTauriApp: boolean
  isPro: boolean
  isOnline: boolean
  readOnly: boolean
  authSession: any

  // External functions
  saveProject: ((data: any, opts?: { debounce?: boolean }) => Promise<void>) | null
  updateProjectName: (name: string) => Promise<void>
  debugLog: (message: string, data?: any) => void

  // Setters
  setProjectName: (v: string) => void
  setCurrentStep: (v: WorkflowStep) => void
  setHasVisitedPowerStep: (v: boolean) => void
  setImportedLEDConfig: (v: any) => void
  setDataPages: (v: DataPage[] | ((prev: DataPage[]) => DataPage[])) => void
  setCurrentDataPageId: (v: string) => void
  setPowerPages: (v: PowerPage[] | ((prev: PowerPage[]) => PowerPage[])) => void
  setCurrentPowerPageId: (v: string) => void
  setExportViewMode: (v: string) => void
  setWallTechnicalConfig: (v: WallTechnicalConfig) => void
  setShowCartouche: (v: boolean) => void
  setCartouche: (v: any) => void
  setBackupPorts: (v: any) => void
  setBackupProcessors: (v: any) => void
  setPortColorOverrides: (v: any) => void
  setOutletColorOverrides: (v: any) => void
  setSavedMires: (v: any) => void
  setProjectMires: (v: any[]) => void
  setMireBackgroundImage: (v: string | null) => void
  setMireBackgroundOpacity: (v: number) => void
  setLocalFilePath: (v: string | null) => void
  setIsSavingDebug: (v: boolean) => void
  setSaveStartTime: (v: number | null) => void
  setLastSaveTime: (v: number | null) => void
  setShowSaveAsDialog: (v: boolean) => void
  setSaveAsFileName: (v: string) => void
}

export function useSaveLoad({
  currentProject,
  projectName,
  currentStep,
  hasVisitedPowerStep,
  importedLEDConfig,
  dataPages,
  currentDataPageId,
  powerPages,
  currentPowerPageId,
  exportViewMode,
  showCartouche,
  cartouche,
  backupPorts,
  backupProcessors,
  portColorOverrides,
  outletColorOverrides,
  savedMires,
  projectMires,
  mireBackgroundImage,
  mireBackgroundOpacity,
  wallTechnicalConfig,
  localFilePath,
  saveAsFileName,
  isTauriApp,
  isPro,
  isOnline,
  readOnly,
  authSession,
  saveProject,
  updateProjectName,
  debugLog,
  setProjectName,
  setCurrentStep,
  setHasVisitedPowerStep,
  setImportedLEDConfig,
  setDataPages,
  setCurrentDataPageId,
  setPowerPages,
  setCurrentPowerPageId,
  setExportViewMode,
  setShowCartouche,
  setCartouche,
  setBackupPorts,
  setBackupProcessors,
  setPortColorOverrides,
  setOutletColorOverrides,
  setSavedMires,
  setProjectMires,
  setMireBackgroundImage,
  setMireBackgroundOpacity,
  setWallTechnicalConfig,
  setLocalFilePath,
  setIsSavingDebug,
  setSaveStartTime,
  setLastSaveTime,
  setShowSaveAsDialog,
  setSaveAsFileName,
}: UseSaveLoadParams) {
  const { t } = useLanguage()

  // Fonction pour collecter toutes les données du synoptique
  const collectSynopticData = useCallback(() => {
    const data = {
      version: "1.0.0",
      type: "synoptic",
      timestamp: new Date().toISOString(),
      projectName: currentProject?.name || projectName,
      currentStep,
      hasVisitedPowerStep,
      importedLEDConfig,
      dataPages,
      currentDataPageId,
      powerPages,
      currentPowerPageId,
      exportViewMode,
      showCartouche,
      cartouche,
      backupPorts,
      backupProcessors,
      portColorOverrides,
      outletColorOverrides,
      savedMires,
      projectMires,
      mireBackgroundImage,
      mireBackgroundOpacity,
      wallTechnicalConfig,
    }
    
    return data
  }, [
    currentProject, projectName, currentStep, hasVisitedPowerStep,
    importedLEDConfig, dataPages, currentDataPageId, powerPages,
    currentPowerPageId, exportViewMode, showCartouche, cartouche, backupPorts, backupProcessors,
    portColorOverrides, outletColorOverrides,
    savedMires, projectMires, mireBackgroundImage, mireBackgroundOpacity, wallTechnicalConfig
  ])

  // Fonction pour sauvegarder dans un fichier local .vgsyn (utilisée par CTRL+S et export)
  const saveToLocalFile = useCallback(async (filePath: string | null, forceSaveAs: boolean = false): Promise<string | null> => {
    try {
      const synopticData = collectSynopticData()
      
      // Debug: vérifier que backupProcessors est bien dans synopticData
      if (synopticData.backupProcessors && Object.keys(synopticData.backupProcessors).length > 0) {
        // console.log('💾 Sauvegarde fichier - backupProcessors inclus:', synopticData.backupProcessors)
      }
      
      const configContent = JSON.stringify(synopticData, null, 2)

      if (isTauriApp) {
        // Tauri: utiliser le dialogue de sauvegarde natif si nécessaire
        const { save } = await import('@tauri-apps/plugin-dialog')
        const { writeTextFile } = await import('@tauri-apps/plugin-fs')

        let finalPath = filePath

        // Si pas de chemin ou force Save As, ouvrir le dialogue
        if (!finalPath || forceSaveAs) {
          const defaultName = `${(currentProject?.name || projectName || 'synoptique').toLowerCase().replace(/[^a-z0-9]/g, '_')}_${new Date().toISOString().slice(0, 10).replace(/-/g, '_')}.vgsyn`
          const selectedPath = await save({
            filters: [{
              name: 'VisualGrid Synoptique',
              extensions: ['vgsyn']
            }],
            defaultPath: defaultName
          })

          if (!selectedPath) {
            return null // Utilisateur a annulé
          }

          finalPath = selectedPath
        }

        // Sauvegarder le fichier
        await writeTextFile(finalPath, configContent)
        setLocalFilePath(finalPath)
        return finalPath
      } else {
        // Web: téléchargement via blob (toujours Save As en web)
        const filename = filePath ? filePath.split(/[/\\]/).pop() || `${(currentProject?.name || projectName || 'synoptique').toLowerCase().replace(/[^a-z0-9]/g, '_')}_${new Date().toISOString().slice(0, 10).replace(/-/g, '_')}.vgsyn` : `${(currentProject?.name || projectName || 'synoptique').toLowerCase().replace(/[^a-z0-9]/g, '_')}_${new Date().toISOString().slice(0, 10).replace(/-/g, '_')}.vgsyn`

        const configBlob = new Blob([configContent], { type: 'application/json' })
        const url = URL.createObjectURL(configBlob)

        const link = document.createElement('a')
        link.href = url
        link.download = filename
        link.click()

        setTimeout(() => URL.revokeObjectURL(url), 100)

        // En web, on ne peut pas stocker le chemin, mais on peut stocker le nom
        setLocalFilePath(filename)
        return filename
      }
    } catch (error) {
      // console.error('Erreur lors de la sauvegarde locale:', error)
      toast.error(t('synoptic.saveLocalFileError') || "Erreur lors de la sauvegarde du fichier local")
      return null
    }
  }, [collectSynopticData, currentProject, projectName, isTauriApp, t, setLocalFilePath])

  // Fonction pour exporter le synoptique en fichier .vgsyn (toujours Save As)
  const handleExportSynopticFile = useCallback(async () => {
    await saveToLocalFile(null, true)
    toast.success(t('synoptic.exportedToFile') || 'Synoptique exporté en fichier .vgsyn')
  }, [saveToLocalFile, t])

  // 🚀 NOUVELLE FONCTION DE SAUVEGARDE : Local en priorité, Cloud en parallèle
  const handleSaveSynoptic = useCallback(async () => {
    const startTime = Date.now()
    setSaveStartTime(startTime)
    setIsSavingDebug(true)

    debugLog('🚀 DÉBUT SAUVEGARDE (Local priorité)', {
      isPro,
      hasCurrentProject: !!currentProject,
      hasLocalFilePath: !!localFilePath,
      projectId: currentProject?.id,
      isOnline,
      timestamp: new Date().toISOString()
    })

    try {
      // 📦 Collecter les données UNE SEULE FOIS
      const collectStartTime = Date.now()
      const synopticData = collectSynopticData()
      debugLog('📦 Données collectées', {
        duration: Date.now() - collectStartTime,
        dataSize: JSON.stringify(synopticData).length
      })

      // 💾 1. SAUVEGARDE LOCAL (PRIORITÉ ABSOLUE - GARANTIE)
      debugLog('💾 DÉBUT SAUVEGARDE LOCAL PRIORITAIRE', {
        hasPath: !!localFilePath,
        isTauriApp
      })

      const localStartTime = Date.now()
      const localSavePromise = saveToLocalFile(localFilePath, false)

      // Attendre la sauvegarde locale (doit réussir)
      const savedPath = await localSavePromise

      setLastSaveTime(Date.now()) // Mettre à jour le timestamp de sauvegarde

      debugLog('✅ SAUVEGARDE LOCAL TERMINÉE', {
        duration: Date.now() - localStartTime,
        savedPath
      })

      // 🎉 Succès local immédiat - l'utilisateur est content
      toast.success(t('synoptic.toast.savedLocally'), {
        description: savedPath ? t('synoptic.toast.savedLocallyDesc').replace('{filename}', savedPath.split(/[/\\]/).pop() || '') : t('synoptic.toast.savedLocally')
      })
      
      // ✅ PostHog: Track project save
      posthog.capture('synoptic:save_project', {
        is_pro: isPro,
        data_pages_count: dataPages.length,
        power_pages_count: powerPages.length,
        has_visited_power_step: hasVisitedPowerStep,
        is_cloud_save: isPro && currentProject && saveProject && isOnline,
      });

      // ☁️ 2. SAUVEGARDE CLOUD EN PARALLÈLE (non bloquant)
      if (isPro && currentProject && saveProject && isOnline) {
        debugLog(' DÉBUT SAUVEGARDE CLOUD EN PARALLÈLE')

        // Lancer la sauvegarde Cloud en arrière-plan sans bloquer
        const cloudSave = async () => {
          try {
            // Vérification rapide de session
            if (!authSession) {
              debugLog(' Session Cloud inactive (no session)')
              toast.warning(t('synoptic.toast.cloudSessionWarning'), {
                description: t('synoptic.toast.cloudSessionWarningDesc')
              })
              return
            }

            const cloudStartTime = Date.now()

            // Timeout pour la sauvegarde Cloud
            const cloudPromise = saveProject(synopticData, { debounce: false })
            const timeoutPromise = new Promise((_, reject) => {
              setTimeout(() => reject(new Error('⏰ TIMEOUT Cloud')), 15000)
            })

            await Promise.race([cloudPromise, timeoutPromise])

            debugLog('✅ SAUVEGARDE CLOUD RÉUSSIE', {
              duration: Date.now() - cloudStartTime,
              projectId: currentProject.id
            })

            // Notification de succès Cloud (subtile)
            toast.success(t('synoptic.toast.savedToCloud'), {
              duration: 3000
            })

          } catch (cloudError) {
            const cloudStartTime = Date.now()
            debugLog('❌ ERREUR SAUVEGARDE CLOUD', {
              duration: Date.now() - cloudStartTime,
              error: cloudError instanceof Error ? {
                name: cloudError.name,
                message: cloudError.message
              } : cloudError
            })

            // Notification d'erreur Cloud (subtile, sans stresser l'utilisateur)
            if (cloudError instanceof Error) {
              if (cloudError.message.includes('TIMEOUT')) {
                toast.warning(t('synoptic.toast.cloudTimeout'), {
                  description: t('synoptic.toast.cloudTimeoutDesc'),
                  duration: 4000
                })
              } else if (cloudError.message.includes('network') || cloudError.message.includes('fetch')) {
                toast.warning(t('synoptic.toast.cloudNetwork'), {
                  description: t('synoptic.toast.cloudNetworkDesc'),
                  duration: 4000
                })
              } else if (cloudError.message.includes('auth') || cloudError.message.includes('token')) {
                toast.warning(t('synoptic.toast.cloudSession'), {
                  description: t('synoptic.toast.cloudSessionDesc'),
                  duration: 4000
                })
              }
            }
          }
        }

        // Lancer la sauvegarde Cloud en arrière-plan
        cloudSave() // Pas de await pour ne pas bloquer
      } else {
        debugLog('☁️ SAUVEGARDE CLOUD IGNORÉE', {
          reason: !isPro ? 'pas Pro' : !currentProject ? 'pas de projet' : !saveProject ? 'pas de fonction' : !isOnline ? 'hors connexion' : 'autre'
        })
      }

      const totalDuration = Date.now() - startTime
      debugLog('🎉 SAUVEGARDE PRIORITAIRE TERMINÉE', {
        totalDuration,
        localSaved: !!savedPath,
        cloudInitiated: !!(isPro && currentProject && saveProject && isOnline)
      })

    } catch (error) {
      const duration = Date.now() - startTime
      debugLog('❌ ERREUR SAUVEGARDE', {
        duration,
        error: error instanceof Error ? {
          name: error.name,
          message: error.message,
          stack: error.stack
        } : error,
        isOnline,
        projectId: currentProject?.id
      })

      // 🚨 Messages d'erreur spécifiques selon le type d'erreur
      if (error instanceof Error) {
        if (error.message.includes('TIMEOUT')) {
          toast.error(t('synoptic.toast.timeoutError'))
        } else if (error.message.includes('network') || error.message.includes('fetch')) {
          toast.error(t('synoptic.toast.networkError'))
        } else if (error.message.includes('auth') || error.message.includes('token')) {
          toast.error(t('synoptic.toast.authError'))
        } else {
          toast.error(t('synoptic.saveError') || "Erreur lors de la sauvegarde")
        }
      }
    } finally {
      setIsSavingDebug(false)
      setSaveStartTime(null)
    }
  }, [collectSynopticData, isPro, currentProject, saveProject, localFilePath, saveToLocalFile, t, debugLog, isOnline, authSession, isTauriApp, setIsSavingDebug, setSaveStartTime, setLastSaveTime])

  // Fonction Save As (ouvre le dialogue)
  const handleSaveAsSynoptic = useCallback(() => {
    setSaveAsFileName(currentProject?.name || projectName || '')
    setShowSaveAsDialog(true)
  }, [currentProject, projectName, setSaveAsFileName, setShowSaveAsDialog])

  // Fonction pour confirmer Save As
  const handleSaveAsConfirm = useCallback(async () => {
    if (!saveAsFileName.trim()) {
      toast.error(t('synoptic.dialog.projectNamePlaceholder') || 'Veuillez entrer un nom de projet')
      return
    }

    try {
      const synopticData = collectSynopticData()

      // 1. Sauvegarder dans le Cloud (si Pro) - mettre à jour le nom si nécessaire
      if (isPro && currentProject && saveProject) {
        // Mettre à jour le nom du projet si différent
        if (saveAsFileName.trim() !== currentProject.name) {
          await updateProjectName(saveAsFileName.trim())
        }
        await saveProject(synopticData, { debounce: false })
      }

      // 2. Sauvegarder dans un nouveau fichier local
      const savedPath = await saveToLocalFile(null, true)

      if (savedPath) {
        toast.success(t('synoptic.savedAs') || 'Projet sauvegardé sous un nouveau nom')
        setShowSaveAsDialog(false)
        setSaveAsFileName('')
      }
    } catch (error) {
      // console.error('Erreur lors de Save As:', error)
      toast.error(t('synoptic.saveAsError') || "Erreur lors de la sauvegarde")
    }
  }, [collectSynopticData, isPro, currentProject, saveProject, updateProjectName, saveAsFileName, saveToLocalFile, t, setShowSaveAsDialog, setSaveAsFileName])

  // Raccourci clavier CTRL+S
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault()
        if (!readOnly) {
          handleSaveSynoptic()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleSaveSynoptic, readOnly])

  // Fonction pour restaurer les données du synoptique
  const restoreSynopticData = useCallback((data: any) => {
    try {
      // Restaurer le nom du projet (mode local seulement)
      if (data.projectName) {
        if (!currentProject) {
          setProjectName(data.projectName)
        }
      }

      // Restaurer l'étape courante
      if (data.currentStep) {
        setCurrentStep(data.currentStep)
      }

      // Restaurer hasVisitedPowerStep
      if (data.hasVisitedPowerStep !== undefined) {
        setHasVisitedPowerStep(data.hasVisitedPowerStep)
      }

      // Restaurer la configuration de mire importée
      if (data.importedLEDConfig) {
        setImportedLEDConfig(data.importedLEDConfig)
      }

      // Restaurer les pages Data — assainies (dédup connexions, orphelins,
      // câbles inter-mode, points invalides)
      if (data.dataPages && Array.isArray(data.dataPages)) {
        const updatedDataPages = data.dataPages.map((page: DataPage) => sanitizeDataPage({
          ...page,
          workspaceElements: page.workspaceElements || [],
          cableRoutes: page.cableRoutes || [],
          panelConnections: page.panelConnections || [],
          mireConnections: page.mireConnections || []
        }))
        setDataPages(updatedDataPages)
      }

      if (data.currentDataPageId) {
        setCurrentDataPageId(data.currentDataPageId)
      }

      // Restaurer les pages Power — assainies
      if (data.powerPages && Array.isArray(data.powerPages)) {
        const updatedPowerPages = data.powerPages.map((page: PowerPage) => sanitizePowerPage({
          ...page,
          workspaceElements: page.workspaceElements || [],
          cableRoutes: page.cableRoutes || [],
          powerConnections: page.powerConnections || [],
          mirePowerConnections: page.mirePowerConnections || []
        }))
        setPowerPages(updatedPowerPages)
      }

      if (data.currentPowerPageId) {
        setCurrentPowerPageId(data.currentPowerPageId)
      }

      // Restaurer les options d'export
      if (data.exportViewMode) {
        setExportViewMode(data.exportViewMode)
      }

      if (data.showCartouche !== undefined) {
        setShowCartouche(data.showCartouche)
      }

      if (data.cartouche) {
        setCartouche(data.cartouche)
      }

      // Restaurer les backupPorts
      if (data.backupPorts) {
        setBackupPorts(data.backupPorts)
      }

      // Restaurer les backupProcessors
      if (data.backupProcessors) {
        setBackupProcessors(data.backupProcessors)
      } else {
        setBackupProcessors({})
      }

      // Restaurer les overrides de couleurs de ports
      if (data.portColorOverrides) setPortColorOverrides(data.portColorOverrides)
      else setPortColorOverrides({})
      if (data.outletColorOverrides) setOutletColorOverrides(data.outletColorOverrides)
      else setOutletColorOverrides({})

      // Restaurer les mires sauvegardées
      if (data.savedMires) {
        setSavedMires(data.savedMires)
      }

      // Restaurer l'image de fond de la mire
      if (data.mireBackgroundImage !== undefined) {
        setMireBackgroundImage(data.mireBackgroundImage)
      }

      // Restaurer la configuration technique du mur avec validation
      if (data.wallTechnicalConfig) {
        const config = data.wallTechnicalConfig
        const validDepths = [8, 10, 12, 16] as const
        const validRates = [50, 60] as const
        
        if (validDepths.includes(config.colorDepth) && validRates.includes(config.refreshRate)) {
          setWallTechnicalConfig(config)
        } else {
          console.warn('Invalid wallTechnicalConfig, using defaults')
        }
      }

      if (data.mireBackgroundOpacity !== undefined) {
        setMireBackgroundOpacity(data.mireBackgroundOpacity)
      }

      return true
    } catch (error) {
      // console.error('Erreur lors de la restauration:', error)
      return false
    }
  }, [currentProject, setProjectName, setCurrentStep, setHasVisitedPowerStep, setImportedLEDConfig, setDataPages, setCurrentDataPageId, setPowerPages, setCurrentPowerPageId, setExportViewMode, setShowCartouche, setCartouche, setBackupPorts, setBackupProcessors, setPortColorOverrides, setOutletColorOverrides, setSavedMires, setMireBackgroundImage, setMireBackgroundOpacity, setWallTechnicalConfig])

  // Fonction pour importer un synoptique depuis un fichier .vgsyn
  const handleImportSynopticFile = useCallback(async () => {
    try {
      if (isTauriApp) {
        // Tauri: utiliser le dialogue d'ouverture natif
        try {
          const { open } = await import('@tauri-apps/plugin-dialog')
          const { readTextFile } = await import('@tauri-apps/plugin-fs')

          const filePath = await open({
            multiple: false,
            filters: [{
              name: 'VisualGrid Synoptique',
              extensions: ['vgsyn']
            }]
          })

          if (filePath && typeof filePath === 'string') {
            // Vérifier l'extension
            if (!filePath.toLowerCase().endsWith('.vgsyn')) {
              toast.error(t('synoptic.invalidFileExtension') || 'Seuls les fichiers .vgsyn sont acceptés')
              return
            }

            const content = await readTextFile(filePath)
            const data = JSON.parse(content)

            // Vérifier que c'est bien un synoptique
            if (data.type !== 'synoptic') {
              toast.error(t('synoptic.invalidFormat') || 'Format de fichier invalide (doit être un synoptique VisualGrid)')
              return
            }

            if (restoreSynopticData(data)) {
              // Sauvegarder le chemin du fichier importé pour les futures sauvegardes
              setLocalFilePath(filePath)
              // Forcer l'étape Data si le projet importé contient des données
              if (data.dataPages || data.powerPages || data.processors || data.panels) {
                setCurrentStep('data')
              }
              toast.success(t('synoptic.importedFromFile') || 'Synoptique importé avec succès')
            } else {
              toast.error(t('synoptic.importError') || "Erreur lors de l'import du synoptique")
            }
          }
        } catch (error) {
          // console.error('Erreur import Tauri:', error)
          toast.error(t('synoptic.importFileError') || "Erreur lors de l'import du fichier")
        }
      } else {
        // Web: input file
        const input = document.createElement('input')
        input.type = 'file'
        input.accept = '.vgsyn'

        input.onchange = async (event) => {
          const file = (event.target as HTMLInputElement).files?.[0]
          if (!file) return

          // Vérifier l'extension
          if (!file.name.toLowerCase().endsWith('.vgsyn')) {
            toast.error(t('synoptic.invalidFileExtension') || 'Seuls les fichiers .vgsyn sont acceptés')
            return
          }

          try {
            const content = await file.text()
            const data = JSON.parse(content)

            // Vérifier que c'est bien un synoptique
            if (data.type !== 'synoptic') {
              toast.error(t('synoptic.invalidFormat') || 'Format de fichier invalide (doit être un synoptique VisualGrid)')
              return
            }

            if (restoreSynopticData(data)) {
              // En web, on stocke le nom du fichier
              setLocalFilePath(file.name)
              // Forcer l'étape Data si le projet importé contient des données
              if (data.dataPages || data.powerPages || data.processors || data.panels) {
                setCurrentStep('data')
              }
              toast.success((t('synoptic.importedFromFile') || 'Synoptique "{name}" importé avec succès').replace('{name}', data.projectName || file.name))
            } else {
              toast.error(t('synoptic.importError') || "Erreur lors de l'import du synoptique")
            }
          } catch (parseError) {
            // console.error('Erreur de parsing:', parseError)
            toast.error(t('synoptic.invalidFile') || 'Fichier de synoptique invalide')
          }
        }

        input.click()
      }
    } catch (error) {
      // console.error('Erreur lors de l\'import:', error)
      toast.error(t('synoptic.importFileError') || "Erreur lors de l'import du fichier")
    }
  }, [isTauriApp, restoreSynopticData, t, setLocalFilePath, setCurrentStep])

  return {
    collectSynopticData,
    saveToLocalFile,
    handleExportSynopticFile,
    handleSaveSynoptic,
    handleSaveAsSynoptic,
    handleSaveAsConfirm,
    restoreSynopticData,
    handleImportSynopticFile,
  }
}
